import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { connect } from "react-redux";

import { faEdit, faEye, faEyeSlash, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ActionIcon, Alert, Badge, Box, Button, Card, Group, Modal, Stack, Table, Text, Title } from "@mantine/core";
import { useNavigate, useParams } from "react-router";
import { bindActionCreators } from "redux";
import { getManageableAssociations } from "../../../data/actions/associationActions";
import {
  createShift,
  getManageableShifts,
} from "../../../data/actions/shiftActions";
import { IShiftState, IState } from "../../../data/reducers/state";
import { IAssociation } from "../../../types/association/Association";
import {
  IShift,
  OrderingType,
  ShiftDaysOfWeek,
} from "../../../types/shifts/Shift";
import { toDateTime } from "../../../util/date+helper";
import { CenteredLoader } from "../../common/loader";
import ShiftEditor from "./ShiftEditor/ShiftEditor";

interface IShiftManagerProps {
  selfId: string;

  manageableShifts: IShift[];
  adminAssociations: string[];
  associations: { [key: string]: IAssociation };
  shiftsMap: { [key: string]: IShift };

  history: any;
  match: any;

  // Direct props - optional, used as filter when provided
  selectedAssociation?: IAssociation | null;

  actions: {
    getManageableShifts: () => Promise<any>;
    getManageableAssociations: () => Promise<any>;
    createShift: (shift: Partial<IShift>) => Promise<IShift>;
  };
}


const ShiftManager: React.FunctionComponent<IShiftManagerProps> = (props) => {
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const params = useParams();
  const navigate = useNavigate();

  const { shiftId } = params;

  React.useEffect(() => {
    document.title = "Stadium Supervisor - Edit shifts";
    props.actions
      .getManageableShifts()
      .then((_) => setLoading(false))
      .catch((er) => setError(er.message));

    props.actions.getManageableAssociations();
  }, []);

  const onShiftClick = (shift: IShift | null) => {
    if (shift) {
      navigate(`/admin/shifts/${shift._id}`);
      document.title = `Stadium Supervisor - Edit ${shift.name}`;
    } else {
      navigate("/admin/shifts");
      document.title = "Stadium Supervisor - Edit";
    }
  };

  const getAssociationNameById = (associationId) => {
    if (!(associationId in props.associations)) {
      return (
        <td style={{ padding: "dense" }}>
          <CenteredLoader size={10} />
        </td>
      );
    }
    return (
      <td style={{ padding: "dense" }}>
        {props.associations[associationId].name}
      </td>
    );
  };

  const addNewShift = () => {
    const value = prompt("Name of the new shift");
    if (!value) return;

    setLoading(true);
    const dayOfWeek = ShiftDaysOfWeek.guessDayOfWeekInString(value);
    const ordering = {
      type: dayOfWeek ? OrderingType.dayOfWeek : OrderingType.custom,
      value: dayOfWeek
        ? ShiftDaysOfWeek.getShiftPriorityFromDayOfWeek(dayOfWeek)
        : 100,
    };
    const shiftToCreate: Partial<IShift> = {
      name: value,
      associationId: props.selectedAssociation?._id || props.adminAssociations[0],
      ordering,
    };
    props.actions
      .createShift(shiftToCreate)
      .then((newShift) => {
        setLoading(false);
        onShiftClick(newShift);
      })
      .catch((er) => alert(er));
  };

  const { manageableShifts, selectedAssociation } = props;

  // Filter shifts by selected association if provided
  const filteredShifts = selectedAssociation
    ? (manageableShifts || []).filter(s => s.associationId === selectedAssociation._id)
    : (manageableShifts || []);

  const displayAssociationName =
    !selectedAssociation && Array.from(new Set(filteredShifts?.map((s) => s.associationId))).length > 1;

  const allShifts = _.orderBy(filteredShifts, (s) => s.finish, "desc");
  const pastShifts = allShifts.filter(
    (s) => !s.finish || toDateTime(s.finish) < DateTime.now(),
  );
  const currentShifts = _.orderBy(
    allShifts.filter(
      (s) => !s.finish || toDateTime(s.finish) >= DateTime.now(),
    ),
    (s) =>
      `${s.associationId}-${new String(s?.ordering?.value || 100).padStart(
        4,
        "0",
      )}-${s?.start}`,
    "asc",
  );

  // Mobile card layout for each shift
  const shiftCardMapper = (shift: IShift) => {
    const start = shift.start ? toDateTime(shift.start) : null;
    const finish = shift.finish ? toDateTime(shift.finish) : null;
    const isActive = !finish || finish >= DateTime.now();

    const onClick = () => onShiftClick(shift);

    return (
      <Card key={`shift-card-${shift._id}`} withBorder p="md" style={{ cursor: 'pointer' }}>
        <Group justify="space-between" align="flex-start">
          <div style={{ flex: 1 }}>
            <Group gap="sm" align="center" mb="xs">
              <Text fw={500} size="sm" c={isActive ? "blue" : "dimmed"}>
                {shift.name}
              </Text>
              {isActive && (
                <Badge color="green" variant="light" size="sm">
                  Active
                </Badge>
              )}
            </Group>

            {displayAssociationName && (
              <Text size="xs" c="dimmed" mb="sm">
                {getAssociationNameById(shift.associationId)}
              </Text>
            )}

            <Group gap="xl">
              <div>
                <Text size="xs" c="dimmed" mb={2}>Start Date</Text>
                <Text size="sm" fw={500}>
                  {start ? start.toLocaleString(DateTime.DATE_SHORT) : "Not set"}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" mb={2}>End Date</Text>
                <Text size="sm" fw={500}>
                  {finish ? finish.toLocaleString(DateTime.DATE_SHORT) : "Not set"}
                </Text>
              </div>
            </Group>
          </div>

          <ActionIcon
            color="blue"
            variant="light"
            onClick={onClick}
            size="lg"
            radius="md"
          >
            <FontAwesomeIcon icon={faEdit} size="sm" />
          </ActionIcon>
        </Group>
      </Card>
    );
  };

  // Desktop table row layout
  const shiftCellMapper = (shift: IShift) => {
    const start = shift.start ? toDateTime(shift.start) : null;
    const finish = shift.finish ? toDateTime(shift.finish) : null;
    const isActive = !finish || finish >= DateTime.now();

    const onClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      onShiftClick(shift);
    };

    return (
      <Table.Tr key={`shift-edit-${shift._id}`} style={{ cursor: 'pointer' }}>
        <Table.Td>
          <Group gap="sm" align="center">
            <div>
              <Text fw={500} size="sm">
                {shift.name}
              </Text>
              {displayAssociationName && (
                <Text size="xs" c="dimmed">
                  {getAssociationNameById(shift.associationId)}
                </Text>
              )}
            </div>
            {isActive && (
              <Badge color="green" variant="light" size="sm">
                Active
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              {start ? start.toLocaleString(DateTime.DATE_SHORT) : "Not set"}
            </Text>

          </Stack>
        </Table.Td>
        <Table.Td>
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              {finish ? finish.toLocaleString(DateTime.DATE_SHORT) : "Not set"}
            </Text>

          </Stack>
        </Table.Td>
        <Table.Td>
          <ActionIcon
            color="blue"
            variant="light"
            onClick={onClick}
            size="lg"
            radius="md"
          >
            <FontAwesomeIcon icon={faEdit} size="sm" />
          </ActionIcon>
        </Table.Td>
      </Table.Tr>
    );
  };

  const shiftTable = (shifts: IShift[]) => {
    if (loading) return <CenteredLoader />;
    if (!shifts || shifts.length === 0) {
      return (
        <Card p="xl" withBorder>
          <Stack align="center" gap="md">
            <Text size="lg" c="dimmed" ta="center">
              No shifts found
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Create your first shift to get started managing officials
            </Text>
          </Stack>
        </Card>
      );
    }

    return (
      <>
        {/* Mobile Card Layout */}
        <Box hiddenFrom="md">
          <Stack gap="sm">
            {shifts.map(shiftCardMapper)}
          </Stack>
        </Box>

        {/* Desktop Table Layout */}
        <Box visibleFrom="md">
          <Card withBorder>
            <Table highlightOnHover striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Text fw={600} size="sm">
                      Shift Details
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text fw={600} size="sm">
                      Start Date
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text fw={600} size="sm">
                      End Date
                    </Text>
                  </Table.Th>
                  <Table.Th w={80}>
                    <Text fw={600} size="sm">
                      Edit
                    </Text>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>{shifts.map(shiftCellMapper)}</Table.Tbody>
            </Table>
          </Card>
        </Box>
      </>
    );
  };

  return (
    <>
      <Stack gap="lg" >
        {error && <Alert color="red">{error}</Alert>}

        {/* Header Section */}

        <Button
          leftSection={<FontAwesomeIcon icon={faPlus} />}
          color="blue"
          onClick={addNewShift}
          size="md"
        >
          New Shift
        </Button>


        {/* Current Shifts Table */}
        {shiftTable(currentShifts)}

        {/* Past Shifts Section */}
        {pastShifts.length > 0 && (
          <Stack gap="md">
            <Group>
              <Button
                variant="subtle"
                leftSection={<FontAwesomeIcon icon={isExpanded ? faEyeSlash : faEye} />}
                onClick={() => setIsExpanded(!isExpanded)}
                color="gray"
              >
                {isExpanded ? "Hide" : "Show"} past shifts ({pastShifts.length})
              </Button>
            </Group>

            {isExpanded && (
              <div>
                <Card withBorder p="md" mb="md" bg="gray.0">
                  <Title order={4} c="dimmed">
                    Past Shifts
                  </Title>
                  <Text size="sm" c="dimmed">
                    Previously completed shifts for reference
                  </Text>
                </Card>
                {shiftTable(pastShifts)}
              </div>
            )}
          </Stack>
        )}
      </Stack>

      {shiftId && (
        <Modal
          size="lg"
          opened={shiftId != null}
          onClose={() => onShiftClick(null)}
          title="Edit Shift"
        >
          <ShiftEditor shiftId={shiftId} />
        </Modal>
      )}
    </>
  );
};

function mapStateToProps(state: IState, ownProps: IShiftManagerProps) {
  const shiftsState: IShiftState = state.shifts;
  return {
    manageableShifts:
      shiftsState.manageable &&
      shiftsState.manageable.map((s) => shiftsState.map[s]),
    shiftsMap: shiftsState.map,
    associations: state.associations.map,
    adminAssociations: state.associations.admin,
    selfId: state.persons.self,
  };
}

function mapDispatchToProps(dispatch): Partial<IShiftManagerProps> {
  return {
    actions: bindActionCreators(
      {
        getManageableShifts,
        getManageableAssociations,
        createShift,
      },
      dispatch,
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ShiftManager);
